/**
 * Shared utilities for processing form answers.
 * Used by FormAnswersViewer (React) and FarmDetailsPDF (react-pdf).
 */

import { Form } from '@/api/types/forms';

export interface ProcessedAnswer {
  answerId: string;
  questionId: string;
  value: any;
  forEachContextValue?: string | null;
  parentContextId?: string | null;
}

export interface ForEachAnswerGroup {
  contextValue: string;
  parentContextId?: string;
  answers: Record<string, any>;
}

/**
 * Parse "invalid JSON" array format like "[Seco, Baba]" or "[Seco]"
 * This format is used by some legacy data where strings aren't quoted.
 */
export function parseInvalidJSONArray(str: string): string[] | string {
  const inner = str.slice(1, -1).trim();
  if (!inner) return [];

  const items = inner.split(',').map(s => s.trim()).filter(s => s !== '');
  return items.length === 1 ? items[0] : items;
}

/**
 * Safely parse answer values that might be stored as JSON strings.
 * Handles both valid JSON and the "invalid JSON" format like "[Seco, Baba]".
 */
export function parseAnswerValue(value: any): any {
  if (typeof value === 'string') {
    if ((value.startsWith('[') && value.endsWith(']')) ||
        (value.startsWith('{') && value.endsWith('}'))) {
      try {
        return JSON.parse(value);
      } catch {
        // Not valid JSON, try parsing the "invalid JSON" format
        if (value.startsWith('[') && value.endsWith(']')) {
          return parseInvalidJSONArray(value);
        }
        return value;
      }
    }
  }
  return value;
}

/**
 * Process raw answers from the API into a standardized format.
 * Converts JSON strings to actual objects/arrays.
 */
export function processAnswers(answers: any[]): ProcessedAnswer[] {
  return answers.map((a: any) => ({
    answerId: a.id,
    questionId: a.questionId,
    value: parseAnswerValue(a.value),
    forEachContextValue: a.forEachContextValue,
    parentContextId: a.parentContextId,
  }));
}

/**
 * Separate regular answers from FOR_EACH answers.
 * Returns an object with regular answers keyed by questionId and an array of FOR_EACH answers.
 */
export function separateAnswers(processedAnswers: ProcessedAnswer[]): {
  regularAnswers: Record<string, any>;
  forEachAnswers: ProcessedAnswer[];
} {
  const regularAnswers: Record<string, any> = {};
  const forEachAnswers: ProcessedAnswer[] = [];

  processedAnswers.forEach((answer) => {
    if (answer.questionId && answer.value !== undefined && answer.value !== null) {
      if (answer.forEachContextValue) {
        forEachAnswers.push(answer);
      } else {
        regularAnswers[answer.questionId] = answer.value;
      }
    }
  });

  return { regularAnswers, forEachAnswers };
}

/**
 * Group FOR_EACH answers by their context (parentContextId or forEachContextValue).
 */
export function groupForEachAnswers(forEachAnswers: ProcessedAnswer[]): Record<string, ForEachAnswerGroup> {
  const grouped: Record<string, ForEachAnswerGroup> = {};

  forEachAnswers.forEach(answer => {
    const { questionId, forEachContextValue, parentContextId, value } = answer;

    // Create a key that represents the full context path
    const contextKey = parentContextId || forEachContextValue;

    if (!contextKey) return;

    if (!grouped[contextKey]) {
      grouped[contextKey] = {
        contextValue: forEachContextValue || '',
        parentContextId: parentContextId || undefined,
        answers: {}
      };
    }

    grouped[contextKey].answers[questionId] = value;
  });

  return grouped;
}

/**
 * Find the source multiselect question ID for a FOR_EACH question.
 * Handles cases where source_multiselect_id is null by extracting it from parentContextId.
 */
export function findSourceQuestionId(
  forEachQuestion: any,
  parentContextId?: string | null,
  formDefinition?: Form | null
): string | null {
  let sourceQuestionId = forEachQuestion?.display_logic?.source_multiselect_id;

  // If source_multiselect_id is not set, try to extract it from parentContextId
  // parentContextId formats:
  // - StringId format: "questionStringId_value" e.g., "contactos_1"
  // - UUID format: "uuid_value" e.g., "6d5c0013-f4e2-4983-b50a-b6063819eac0_2"
  if (!sourceQuestionId && parentContextId && formDefinition) {
    // First check if it's a UUID format (contains hyphens)
    const uuidMatch = parentContextId.match(/^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})_/i);
    if (uuidMatch) {
      // It's a UUID format - find question by uuid
      const parentUuid = uuidMatch[1];
      const parentQuestion = formDefinition.questions.find(q => q.uuid === parentUuid);
      sourceQuestionId = parentQuestion?.uuid || parentQuestion?.id || null;
    } else {
      // StringId format - extract everything before the last underscore
      const lastUnderscoreIndex = parentContextId.lastIndexOf('_');
      if (lastUnderscoreIndex > 0) {
        const parentStringId = parentContextId.substring(0, lastUnderscoreIndex);
        const parentQuestion = formDefinition.questions.find(q => q.id === parentStringId);
        sourceQuestionId = parentQuestion?.uuid || parentQuestion?.id || null;
      }
    }
  }

  return sourceQuestionId;
}

/**
 * Find a question by its ID (checking both uuid and id fields).
 */
export function findQuestion(
  formDefinition: Form | null,
  questionId: string
): any {
  // FIX: Check both id AND uuid, not just uuid || id
  return formDefinition?.questions.find(q => q.id === questionId || q.uuid === questionId);
}

/**
 * Get the display label for a FOR_EACH context value.
 * Looks up the source multiselect question and finds the matching option label.
 */
export function getForEachContextLabel(
  contextValue: string,
  sourceQuestionId: string | null,
  formDefinition: Form | null
): string {
  if (!sourceQuestionId || !formDefinition) {
    return contextValue;
  }

  const sourceQuestion = findQuestion(formDefinition, sourceQuestionId);
  if (!sourceQuestion?.options || sourceQuestion.options.length === 0) {
    return contextValue;
  }

  // Try exact match first
  let sourceOption = sourceQuestion.options.find((opt: any) => opt.value === contextValue);

  // If not found, try with type conversion (contextValue might be "1" but option value is 1 or vice versa)
  if (!sourceOption) {
    sourceOption = sourceQuestion.options.find((opt: any) => String(opt.value) === String(contextValue));
  }

  return sourceOption?.label || contextValue;
}

/**
 * Get the display label for a FOR_EACH context value from a list of questions.
 * This is a helper for components that have access to the questions array but not the full form definition.
 */
export function getContextLabelFromQuestions(
  contextValue: string,
  sourceQuestionId: string | null,
  allQuestions: any[]
): string {
  if (!sourceQuestionId || !allQuestions || allQuestions.length === 0) {
    return contextValue;
  }

  // FIX: Check both id AND uuid, not just uuid || id
  const sourceQuestion = allQuestions.find(q => q.id === sourceQuestionId || q.uuid === sourceQuestionId);

  if (!sourceQuestion) {
    return contextValue;
  }

  if (!sourceQuestion.options || sourceQuestion.options.length === 0) {
    return contextValue;
  }

  // Try exact match first
  let sourceOption = sourceQuestion.options.find((opt: any) => opt.value === contextValue);

  // If not found, try with type conversion (contextValue might be "1" but option value is 1 or vice versa)
  if (!sourceOption) {
    sourceOption = sourceQuestion.options.find((opt: any) => String(opt.value) === String(contextValue));
  }

  return sourceOption?.label || contextValue;
}

/**
 * Identify source questions that should be hidden (those that have FOR_EACH children).
 */
export function getSourceQuestionsToHide(formDefinition: Form | null): Set<string> {
  const sourceQuestionsToHide = new Set<string>();

  if (!formDefinition) return sourceQuestionsToHide;

  formDefinition.questions.forEach(q => {
    if (q.display_logic?.condition_type === 'for_each' && q.display_logic.source_multiselect_id) {
      sourceQuestionsToHide.add(q.display_logic.source_multiselect_id);
    }
  });

  return sourceQuestionsToHide;
}

/**
 * Filter out FOR_EACH questions and their source multiselect questions.
 */
export function filterRegularQuestions(
  formDefinition: Form | null,
  sourceQuestionsToHide: Set<string>
): any[] {
  if (!formDefinition) return [];

  return formDefinition.questions.filter(q => {
    // Skip FOR_EACH questions
    if (q.display_logic?.condition_type === 'for_each') return false;
    // Skip questions that are source multiselects for FOR_EACH questions
    if (sourceQuestionsToHide.has(q.uuid || q.id)) return false;
    return true;
  });
}
